import { TemplateSupport, inheritBindingContext } from '../../component-model/template-elements';
import {
	bindable,
	Container,
	ViewCompiler,
	ViewResources,
	autoinject,
	containerless,
	processContent,
	observable
} from 'aurelia-framework';
import { KeyCode } from '../../utils/key-code';
import { debounce } from 'lodash';

@autoinject
@processContent(inheritBindingContext(false))
export class Combobox extends TemplateSupport {
	@bindable options: any[];
	@bindable change: (params: { model: any }) => void;
	@bindable isOpen = false;
	@bindable disabled = false;
	@bindable model: any;
	@bindable search: (params: { searchString: string }) => number;

	@observable searchString: string;

	searchFocused = false;
	focused = 0;
	dropdownMenu: HTMLElement;
	optionElements: HTMLElement[] = [];

	constructor(
		element: Element,
		container: Container,
		compiler: ViewCompiler,
		resources: ViewResources) {

		super(element, container, compiler, resources);
	}

	attached() {
		if (this.model != null) {
			this.focused = this.getModelIndex(this.model);
		}
	}

	toggle() {
		if (this.disabled) {
			return false;
		}

		if (!this.isOpen) {
			this.isOpen = true;
			this.searchFocused = true;
		} else {
			this.close();
		}
	}

	close() {
		this.isOpen = false;
		this.searchString = null;
		this.focused = this.getModelIndex(this.model);
		this.searchFocused = false;
	}

	private getModelIndex(model: any) {
		return this.model != null ? this.options.indexOf(this.model) + 1 : 0;
	}

	select(model: any) {
		this.model = model;
		if (this.change) { this.change({ model }); }
	}

	searchStringChanged(newValue: string, oldValue: string) {
		if (this.search == null) { return; }

		if (!oldValue && newValue) { this.model = null; }

		this.onSearch(newValue, oldValue);
	}

	private onSearch = debounce((searchString: string, oldSearchString: string) => {
		if (!searchString) {
			this.focused = 0;
			return;
		}

		const result = this.search({ searchString });

		if (result === null) {
			this.changeFocus(0, null);
			return;
		}

		this.changeFocus(result + 1, this.optionElements[result + 1]);
	}, 150);

	changeFocus(index: number, element: HTMLElement) {
		this.focused = index;
		this.dropdownMenu.scrollTop = this.getScrollTop(this.dropdownMenu, element);
	}

	private getScrollTop(parent: HTMLElement, child: HTMLElement) {
		if (child == null) {
			return 0;
		}

		if (parent.scrollTop > child.offsetTop) {
			return child.offsetTop;
		}

		if (parent.scrollTop + parent.offsetHeight < child.offsetTop + child.offsetHeight) {
			return child.offsetTop - parent.offsetHeight + child.offsetHeight;
		}

		return parent.scrollTop;
	}

	checkKey(event: KeyboardEvent) {
		switch (event.keyCode) {
			case KeyCode.UpArrow:
				this.moveUp();
				return false;
			case KeyCode.DownArrow:
				this.moveDown();
				return false;
			case KeyCode.Enter:
				this.selectFocused();
				return false;
			case KeyCode.Esc:
				this.close();
				return false;
			default:
				return true;
		}
	}

	moveUp() {
		if (this.focused === 0) {
			return;
		}

		const newFocus = this.focused - 1;
		this.changeFocus(newFocus, this.optionElements[newFocus]);
	}

	moveDown() {
		if (this.focused + 1 >= this.options.length) {
			return;
		}

		const newFocus = this.focused + 1;
		this.changeFocus(newFocus, this.optionElements[newFocus]);
	}

	selectFocused() {
		const option = this.focused > 0 ? this.options[this.focused - 1] : null;
		this.select(option);
		this.close();
	}

	addOptionElement(index: number, element: HTMLElement) {
		this.optionElements[index] = element;
	}
}
